// eslint-disable-next-line @eslint-community/eslint-comments/disable-enable-pair
/* eslint-disable @typescript-eslint/naming-convention */
import typia from 'typia'
import { type Router } from 'vue-router'

import { isBoundDeviceState } from '@/components/newProductPanel/newProductPanel.type'
import {
  type BoundDeviceCategoryType,
  type UnboundDeviceCategoryType,
} from '@/graphql/creator-platform/generated'
import { type ProductStateWithSelectedProduct } from '@/store/newProductStore.type'

export interface BoundDeviceProductQueryString {
  readonly product_type: BoundDeviceCategoryType

  readonly device_handle: string

  readonly device_color?: string | undefined

  readonly product_handle: string

  readonly product_color: string
}

export interface UnboundDeviceProductQueryString {
  readonly product_type: UnboundDeviceCategoryType

  readonly product_handle: string

  readonly product_color: string
}

export function extractProductQueryString(
  obj: object,
): BoundDeviceProductQueryString | null | UnboundDeviceProductQueryString {
  if (typia.is<BoundDeviceProductQueryString>(obj)) {
    const clone = structuredClone(obj)
    typia.misc.prune<BoundDeviceProductQueryString>(clone)
    return clone
  }
  if (typia.is<UnboundDeviceProductQueryString>(obj)) {
    const clone = structuredClone(obj)
    typia.misc.prune<UnboundDeviceProductQueryString>(clone)
    return clone
  }
  return null
}

export const OptionalQueryString = {
  artworkType: 'artwork_type',
  carrierBarcodeImageUrl: 'carrier_barcode_image_url',
  discountCode: 'discount_code',
  foreignPhotoUrl: 'foreign_photo_url',
  keyword: 'keyword',
  pinnedStickerGroup: 'pinned_sticker_group',
  shareDesign: 'share_design',
  tags: 'tags',
} as const

export type QueryStringInfo = BoundDeviceProductQueryString | UnboundDeviceProductQueryString

export function getProductQueryString(): null | QueryStringInfo {
  const params = Object.fromEntries(new URLSearchParams(window.location.search))
  if (typia.is<BoundDeviceProductQueryString | UnboundDeviceProductQueryString>(params)) {
    return params
  }
  return null
}

export async function setProductQueryString(
  state: ProductStateWithSelectedProduct,
  router: Router,
): Promise<void> {
  await upsertOrDeleteQueryString(
    isBoundDeviceState(state)
      ? {
          product_type: state.categoryType,
          device_handle: state.device.handle,
          device_color: state.color?.handle ?? null,
          product_handle: state.selectedProduct.product.handle,
          product_color: state.selectedProduct.variant.color.handle,
        }
      : {
          product_type: state.categoryType,
          product_handle: state.selectedProduct.product.handle,
          product_color: state.selectedProduct.variant.color.handle,
          device_handle: null,
          device_color: null,
        },
    router,
  )
}

type QsKey =
  | (typeof OptionalQueryString)[keyof typeof OptionalQueryString]
  | keyof BoundDeviceProductQueryString
  | keyof UnboundDeviceProductQueryString

export async function upsertOrDeleteQueryString(
  newQueryString: Partial<Record<QsKey, null | string>>,
  router: Router,
): Promise<void> {
  // 直接用 @router 裡的 router 會造成循環依賴
  const queryMap = new Map(Object.entries(router.currentRoute.value.query))
  for (const [key, value] of Object.entries(newQueryString)) {
    if (typeof value === 'string') {
      const v = key === 'keyword' ? encodeURIComponent(value) : value
      queryMap.set(key, v)
    } else {
      queryMap.delete(key)
    }
  }
  await router.replace({ query: Object.fromEntries(queryMap) })
}

export async function getValidShareDesign(router: Router): Promise<null | string> {
  const shareDesign = router.currentRoute.value.query[OptionalQueryString.shareDesign]
  if (typeof shareDesign !== 'string') {
    return null
  }
  const productQueryStrings = getProductQueryString()
  if (productQueryStrings === null) {
    await upsertOrDeleteQueryString({ share_design: null }, router)
    return null
  }
  return shareDesign
}
